/**
 * Key-light shadows (style guide §3.3; D-047 lighting overhaul): a clipmap of concentric light-space square levels
 * around the shadow focus (the player ship in game, the subject in lookdev). Level 0 hugs the focus sphere for
 * crisp self-shadowing; each next level is `ratio` times wider, so the rocks around the ship, other ships and the
 * derelict a few kilometres out still cast and receive. The camera stays at the render origin (D-011), so the whole
 * clipmap follows the floating origin for free.
 *
 * - Every level is snapped to its own texel grid in the light's basis (`fitShadowFocus`): no edge crawl.
 * - Active levels are cross-faded by light-space containment, coarse to fine; outside every level the key is
 *   unshadowed. G5: inactive levels are skipped by a uniform branch, and a level's taps only run where it contributes
 *   (a per-pixel branch; not on Firefox, whose WGSL keeps derivative_uniformity on and rejects compare samples in
 *   divergent control flow).
 * - Normal and depth bias scale with each level's world texel size.
 * - Filtering is Castaño's optimized 5×5 PCF (MJP "Shadows" sample): nine hardware-bilinear compare taps whose
 *   weights rebuild a smooth tent kernel, so penumbrae are soft and stable without per-pixel noise.
 *
 * Per-frame code (`stepShadowLevels`) is allocation-free (D-014).
 */
import { DirectionalLight, NodeUpdateType, Object3D, ShadowBaseNode, type DepthTexture, type LightShadow, type Node, type NodeBuilder, type UniformNode } from 'three/webgpu'
import { Fn, If, abs, float, floor, lightShadowMatrix, max, mix, reference, renderGroup, shadow, shadowPositionWorld, smoothstep, texture, uniform, vec2, vec4 } from 'three/tsl'
import { SCENE_ONLY, SHADOW_CASTER, SHADOW_NEAR } from '../layers'
import { createShadowFit, fitShadowFocus, type ShadowFit } from './shadowFit'

type F = Node<'float'>

/** Clipmap configuration (environment.json `shadows`). */
export interface ShadowLevelsConfig {
  /** Levels allocated (the High count); presets activate a prefix of them. */
  readonly maxLevels: number
  /** Half-extent growth per level. */
  readonly ratio: number
  /** Level 0 half-extent = focus radius × this. */
  readonly focusScale: number
  /** Smallest level-0 half-extent (metres). */
  readonly minHalfExtent: number
  /** Cross-fade band at each level's border, as a fraction of its half-extent. */
  readonly fade: number
  /** Extra depth (metres) toward the star so casters outside a level still shadow into it. */
  readonly casterMargin: number
  /**
   * SC-6a2: the margin field rocks are selected with (`outerShadowReach`, `shadowLevelBox`; PF-2/PF-2b). The
   * shadow cameras always use `casterMargin`; rocks beyond this depth sunward stay out of the caster sets.
   */
  readonly rockCasterMargin: number
  /**
   * G5 (PERF): branch around each level so a level whose weight is 0 (inactive on this preset) skips its
   * containment maths, and a pixel outside a level (`inside × weight = 0`) skips its 9 PCF taps. Value-identical:
   * the skipped update is `mix(r, x, 0) = r`. False (or absent) = every allocated level sampled per pixel (pre-G5).
   */
  readonly skipZeroLevels?: boolean
  /** Normal bias in world texels of each level. */
  readonly normalBiasTexels: number
  /** Depth bias in world texels of each level. */
  readonly biasTexels: number
}

export interface ShadowLevel {
  /** Stands in for the light (position + target), one per level; never added to the scene. */
  readonly holder: Object3D & { target: Object3D; shadow: LightShadow; castShadow: boolean }
  readonly shadow: LightShadow
  readonly fit: ShadowFit
  /** 1 = level active and blended in, 0 = inactive (never rendered). */
  readonly weight: UniformNode<'float', number>
  half: number
}

export interface ShadowLevels {
  readonly node: ShadowBaseNode
  readonly levels: readonly ShadowLevel[]
  readonly config: ShadowLevelsConfig
  /** Overall shadow strength (1 = no key light in full shadow). */
  readonly intensity: UniformNode<'float', number>
  active: number
  mapSize: number
}

// ---------------------------------------------------------------------------------------------------------------
// Pure maths (tested)
// ---------------------------------------------------------------------------------------------------------------

/** Half-extent (metres) of level `i` around a focus sphere of `radius`. */
export function levelHalfExtent(cfg: ShadowLevelsConfig, radius: number, i: number): number {
  return Math.max(radius * cfg.focusScale, cfg.minHalfExtent) * cfg.ratio ** i
}

/**
 * Castaño's optimized 5×5 PCF weights along one axis for the sub-texel fraction `s` in [0, 1): three taps with
 * weights `w` (sum 12) at offsets `o` (texels from the base texel corner). Writes into `out` = [w0, w1, w2, o0, o1, o2].
 */
export function pcf5Axis(s: number, out: Float64Array): Float64Array {
  const w0 = 4 - 3 * s
  const w1 = 7
  const w2 = 1 + 3 * s
  out[0] = w0
  out[1] = w1
  out[2] = w2
  out[3] = (3 - 2 * s) / w0 - 2
  out[4] = (3 + s) / w1
  out[5] = s / w2 + 2
  return out
}

// ---------------------------------------------------------------------------------------------------------------
// Filter (TSL)
// ---------------------------------------------------------------------------------------------------------------

interface FilterInputs {
  depthTexture: DepthTexture
  shadowCoord: Node<'vec3'>
  shadow: LightShadow
}

/** Optimized 5×5 PCF on a hardware-compare (bilinear) depth texture. */
export const pcfOptimized5 = Fn((args: { readonly [key: string]: unknown }): F => {
  const { depthTexture, shadowCoord, shadow: sh } = args as unknown as FilterInputs
  const mapSize = (reference('mapSize', 'vec2', sh) as unknown as { setGroup(g: unknown): Node<'vec2'> }).setGroup(renderGroup)
  const texel = vec2(1).div(mapSize)
  const uv = shadowCoord.xy.mul(mapSize)
  const base = floor(uv.add(0.5))
  const s = uv.x.add(0.5).sub(base.x)
  const t = uv.y.add(0.5).sub(base.y)
  const baseUv = base.sub(0.5).mul(texel)
  const axis = (f: F): [F, F, F, F, F, F] => {
    const w0 = float(4).sub(f.mul(3))
    const w2 = f.mul(3).add(1)
    return [w0, float(7), w2, float(3).sub(f.mul(2)).div(w0).sub(2), f.add(3).div(7), f.div(w2).add(2)]
  }
  const [uw0, uw1, uw2, u0, u1, u2] = axis(s)
  const [vw0, vw1, vw2, v0, v1, v2] = axis(t)
  const tap = (u: F, v: F): F => texture(depthTexture, baseUv.add(vec2(u, v).mul(texel))).compare(shadowCoord.z) as unknown as F
  const us: [F, F][] = [
    [uw0, u0],
    [uw1, u1],
    [uw2, u2],
  ]
  const vs: [F, F][] = [
    [vw0, v0],
    [vw1, v1],
    [vw2, v2],
  ]
  let sum: F = float(0)
  for (const [vw, v] of vs) for (const [uw, u] of us) sum = sum.add(uw.mul(vw).mul(tap(u, v)))
  return sum.div(144)
}) as unknown as (inputs: FilterInputs) => F

/** One hardware-bilinear compare tap (2×2 PCF): the WebGL2 fallback filter (webgl2.json `shadowFilter`, D-053). */
export const pcfHardware = Fn((args: { readonly [key: string]: unknown }): F => {
  const { depthTexture, shadowCoord } = args as unknown as FilterInputs
  return texture(depthTexture, shadowCoord.xy).compare(shadowCoord.z) as unknown as F
}) as unknown as (inputs: FilterInputs) => F

/** Shadow filter kernels: `pcf5` (Castaño 5×5, WebGPU) or `hardware` (one bilinear compare tap, WebGL2). */
export type ShadowFilter = 'pcf5' | 'hardware'

// ---------------------------------------------------------------------------------------------------------------
// The clipmap shadow node
// ---------------------------------------------------------------------------------------------------------------

class ClipmapShadowNode extends ShadowBaseNode {
  static get type(): string {
    return 'ClipmapShadowNode'
  }

  readonly levels: readonly ShadowLevel[]
  readonly fade: UniformNode<'float', number>
  readonly intensity: UniformNode<'float', number>
  /** G5: skip zero-weight levels (uniform branch) and zero-containment taps (per-pixel branch). */
  readonly skipZero: boolean
  private readonly levelNodes: Node<'float'>[]

  constructor(light: DirectionalLight, levels: readonly ShadowLevel[], fade: number, intensity: number, skipZero = false) {
    super(light)
    this.levels = levels
    this.skipZero = skipZero
    this.fade = uniform(fade)
    this.intensity = uniform(intensity)
    this.levelNodes = levels.map((l) => shadow(l.holder as unknown as DirectionalLight, l.shadow) as unknown as Node<'float'>)
    // Each level's own shadow node renders its map; ShadowBaseNode asks for a per-render updateBefore that this
    // composite does not need (three's abstract Node.updateBefore only warns "Abstract function.").
    this.updateBeforeType = NodeUpdateType.NONE
  }

  override setup(builder: NodeBuilder): Node<'float'> {
    return Fn(() => {
      this.setupShadowPosition(builder)
      const result = float(1).toVar()
      // Coarse to fine: a finer level that contains the point overrides the coarser answer.
      for (let i = this.levels.length - 1; i >= 0; i--) {
        const l = this.levels[i]
        const blend = (): void => {
          // The shadow matrix maps the level's square to [0, 1] UVs: containment is the distance from its centre.
          const uvz = lightShadowMatrix(l.holder as unknown as DirectionalLight).mul(vec4(shadowPositionWorld as unknown as Node<'vec3'>, 1))
          const edge = max(abs(uvz.x.sub(0.5)), abs(uvz.y.sub(0.5))).mul(2)
          const inside = float(1).sub(smoothstep(float(1).sub(this.fade), float(0.995), edge))
          if (!this.skipZero) {
            result.assign(mix(result, this.levelNodes[i], inside.mul(l.weight)))
            return
          }
          // G5: the taps run only where the level contributes; elsewhere mix(r, x, 0) = r, so skipping is exact.
          // Chrome's WGSL turns derivative_uniformity off and the depth maps have one mip, so the compare taps in
          // a non-uniform branch return what they return outside it.
          if (!DIVERGENT_COMPARE_OK) {
            result.assign(mix(result, this.levelNodes[i], inside.mul(l.weight)))
            return
          }
          const k = inside.mul(l.weight).toVar()
          If(k.greaterThan(0), () => {
            result.assign(mix(result, this.levelNodes[i], k))
          })
        }
        // G5: an inactive level (weight 0 on this preset) is skipped by a uniform branch, containment maths included.
        if (this.skipZero) If(l.weight.greaterThan(0), blend)
        else blend()
      }
      return mix(float(1), result, this.intensity)
    })() as unknown as Node<'float'>
  }
}

/**
 * three's WGSL builder turns `derivative_uniformity` off except on Firefox/Deno (WGSLNodeBuilder.js); only there may a
 * compare sample sit in non-uniform control flow (G5's per-pixel tap skip).
 */
const DIVERGENT_COMPARE_OK = !(typeof navigator !== 'undefined' && /Firefox|Deno/.test(navigator.userAgent))

/** PF-2b: the finest levels (0 and 1) also draw the near rock set (`SHADOW_NEAR`); coarser levels never do. */
export const SHADOW_NEAR_LEVELS = 2

function createLevel(sun: DirectionalLight, filter: ShadowFilter, index: number): ShadowLevel {
  const holder = new Object3D() as ShadowLevel['holder']
  holder.target = new Object3D()
  holder.castShadow = true
  const sh = sun.shadow.clone()
  sh.intensity = 1
  sh.autoUpdate = false
  ;(sh as LightShadow & { filterNode?: unknown }).filterNode = filter === 'hardware' ? pcfHardware : pcfOptimized5
  // PF-2: the level camera also draws the shadow-only rock casters and the SCENE_ONLY far rocks (PF-7). A bit other
  // than 0 keeps three from overwriting the mask with the main camera's (ShadowNode.updateShadow).
  sh.camera.layers.enable(SHADOW_CASTER)
  sh.camera.layers.enable(SCENE_ONLY)
  // PF-2b: levels 0-1 also draw the near rocks at their visible LOD (crater-rim shadows at the terminator).
  if (index < SHADOW_NEAR_LEVELS) sh.camera.layers.enable(SHADOW_NEAR)
  holder.shadow = sh
  return { holder, shadow: sh, fit: createShadowFit(), weight: uniform(0), half: 1 }
}

/**
 * Allocates `maxLevels` levels for `sun` and installs the clipmap as the light's shadow node. Every allocated
 * level is compiled into the lit shaders, so the WebGL2 fallback allocates fewer (D-053).
 */
export function createShadowLevels(sun: DirectionalLight, cfg: ShadowLevelsConfig, intensity: number, filter: ShadowFilter = 'pcf5'): ShadowLevels {
  const levels: ShadowLevel[] = []
  for (let i = 0; i < cfg.maxLevels; i++) levels.push(createLevel(sun, filter, i))
  const node = new ClipmapShadowNode(sun, levels, cfg.fade, intensity, cfg.skipZeroLevels === true)
  ;(sun.shadow as LightShadow & { shadowNode?: unknown }).shadowNode = node
  sun.castShadow = true
  return { node, levels, config: cfg, intensity: node.intensity, active: 0, mapSize: 0 }
}

/** Activates the first `count` levels at `mapSize`² (preset change; not per frame). */
export function configureShadowLevels(s: ShadowLevels, count: number, mapSize: number): void {
  s.active = Math.min(count, s.levels.length)
  s.mapSize = mapSize
  for (let i = 0; i < s.levels.length; i++) {
    const l = s.levels[i]
    const on = i < s.active && mapSize > 0
    l.weight.value = on ? 1 : 0
    l.shadow.autoUpdate = on
    if (on) l.shadow.mapSize.set(mapSize, mapSize)
  }
}

/**
 * Fits every active level around the focus sphere (render space) for the light direction (dx, dy, dz) toward the
 * light. Allocation-free.
 */
export function stepShadowLevels(s: ShadowLevels, dx: number, dy: number, dz: number, cx: number, cy: number, cz: number, radius: number): void {
  const cfg = s.config
  for (let i = 0; i < s.active; i++) {
    const l = s.levels[i]
    const half = levelHalfExtent(cfg, radius, i)
    const f = fitShadowFocus(dx, dy, dz, cx, cy, cz, half, s.mapSize, cfg.casterMargin, l.fit)
    l.half = half
    l.holder.target.position.set(f.tx, f.ty, f.tz)
    l.holder.position.set(f.px, f.py, f.pz)
    l.holder.target.updateMatrixWorld()
    l.holder.updateMatrixWorld()
    const cam = l.shadow.camera as unknown as { left: number; right: number; top: number; bottom: number; near: number; far: number; updateProjectionMatrix(): void }
    if (cam.right !== f.half || cam.far !== f.far) {
      cam.left = -f.half
      cam.right = f.half
      cam.bottom = -f.half
      cam.top = f.half
      cam.near = f.near
      cam.far = f.far
      cam.updateProjectionMatrix()
    }
    l.shadow.normalBias = f.texel * cfg.normalBiasTexels
    l.shadow.bias = -(f.texel * cfg.biasTexels) / Math.max(f.far - f.near, 1e-3)
  }
}

/**
 * Field-rock caster reach (PF-2): distance from the shadow focus to the farthest corner of the outer active level's
 * box for rocks (±h across the light, h behind the focus to h + rockCasterMargin toward the light) for a focus of
 * `radius`: a rock farther than this from the focus is not a caster. The shadow cameras reach `casterMargin` deep
 * (SC-6a2: 2,500 m, for km stations); rocks keep `rockCasterMargin`. Infinity when no level is active.
 */
export function outerShadowReach(s: ShadowLevels, radius: number): number {
  if (s.active < 1 || s.mapSize <= 0) return Number.POSITIVE_INFINITY
  const h = levelHalfExtent(s.config, radius, s.active - 1)
  const d = h + s.config.rockCasterMargin
  return Math.sqrt(2 * h * h + d * d)
}

/** One shadow level's box in render space, as `stepShadowLevels` last fitted it (PF-2b). */
export interface ShadowBox {
  /** Box centre on the light axis: the snapped focus. */
  cx: number
  cy: number
  cz: number
  /** Unit direction toward the light. */
  lx: number
  ly: number
  lz: number
  /**
   * Half-extent across the light; along it the box runs from `half` behind the centre to `half + margin` toward the
   * light. `margin` is the rock selection margin (`rockCasterMargin`, SC-6a2); the camera itself reaches `casterMargin`.
   */
  half: number
  margin: number
}

export function createShadowBox(): ShadowBox {
  return { cx: 0, cy: 0, cz: 0, lx: 0, ly: 1, lz: 0, half: 0, margin: 0 }
}

/**
 * Writes level `i`'s last fitted box (clamped to the outer active level) into `out`. False, `out` untouched, when no
 * level is active. Allocation-free.
 */
export function shadowLevelBox(s: ShadowLevels, i: number, out: ShadowBox): boolean {
  if (s.active < 1 || s.mapSize <= 0) return false
  const l = s.levels[Math.min(Math.max(i, 0), s.active - 1)]
  const t = l.holder.target.position
  const p = l.holder.position
  const dx = p.x - t.x
  const dy = p.y - t.y
  const dz = p.z - t.z
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (len <= 0) return false
  out.cx = t.x
  out.cy = t.y
  out.cz = t.z
  out.lx = dx / len
  out.ly = dy / len
  out.lz = dz / len
  out.half = l.half
  out.margin = s.config.rockCasterMargin
  return true
}

/** Every mesh under `root` casts and receives key-light shadows (transparent effects excluded). */
export function enableShadows(root: Object3D): void {
  root.traverse((o) => {
    const m = o as Object3D & { isMesh?: boolean; material?: { transparent?: boolean } | { transparent?: boolean }[] }
    if (!m.isMesh) return
    const mats = Array.isArray(m.material) ? m.material : [m.material]
    const transparent = mats.some((x) => x?.transparent === true)
    m.castShadow = !transparent
    m.receiveShadow = true
  })
}

export function disposeShadowLevels(s: ShadowLevels): void {
  s.node.dispose()
  for (const l of s.levels) l.shadow.dispose()
}
